import React, { useCallback, useContext, useMemo, useRef } from 'react'

import styled from 'styled-components'
import cn from 'clsx'
import { rem } from 'polished'

import CalendarDatePickerSelectBox from './BedbankDatePicker/CalendarDatePickerSelectBox'
import CalendarDatePicker from './BedbankDatePicker/CalendarDatePicker'
import OfferDatePickerSelectBox from '../Filters/DatePicker/OfferDatePickerSelectBox'
import OfferDatePicker from '../Filters/DatePicker/OfferDatePicker'

import config from 'constants/config'

import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import OfferAirportSelectBox from '../Filters/Airport/OfferAirportSelectBox'
import OfferAirportOptions from '../Filters/Airport/OfferAirportOptions'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferCapacityWithAgesSelection from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelection'
import OfferCapacityWithAgesSelectBox from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import { selectIsBedbankFlightBookingEnabled } from 'selectors/flightsSelectors'
import { isFlashCalendarEnabled, getSelectedDay } from 'selectors/offerPage/bedbankOfferPageSelectors'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { getCapacitiesForBedbankOffer } from 'lib/offer/offerUtils'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import useDelayedTogglingBoolean from 'hooks/useDelayedTogglingBoolean'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import TextButton from 'components/Luxkit/Button/TextButton'
import { displayPricingAs, getCalendarDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import { sum } from 'lib/array/arrayUtils'
import eases from 'styles/tools/eases'
import { pluralizeToString } from 'lib/string/pluralize'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import LineArrowRightIcon from 'components/Luxkit/Icons/line/LineArrowRightIcon'
import { OptimizelyExperiments } from 'constants/optimizely'
import BedbankPackageSelectionModal from './MobileBookingFlow/Modals/BedbankPackageSelectionModal'
import BedbankRoomSelectionModal from './MobileBookingFlow/Modals/BedbankRoomSelectionModal'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import BedbankRoomAndPackageDetailsModal from './MobileBookingFlow/Modals/BedbankRoomAndPackageDetailsModal'

const FiltersBlock = styled.div`
  flex-grow: 1;

  > * {
    height: ${rem(64)};
    width: 100%;
    padding: 0 ${rem(24)};

    &:not(:first-child):not(:last-child) {
      box-shadow: 0 -1px 0px 0px ${props => props.theme.palette.neutral.default.five};
    }
  }
`

const FooterContents = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  transform: translateY(100%);
  transition: transform 0.3s ${eases.expo.in};

  &.visible {
    position: unset;
    transform: translateY(0);
    transition: transform 0.3s ${eases.expo.out};
  }
`

const CalendarDatePickerWrapper = styled.div`
  padding-top: ${rem(20)};
`

interface Props {
  activeFilter?: string;
  offer: App.BedbankOffer;
  isCalendarEnabled: boolean;
  onViewPackageOptions?: () => void;
  recommendationsShown: boolean;
  flightsEnabled: boolean;
  selectedDay?: App.BedbankCalendarDay;
  rooms?: Array<App.Occupants>;
  duration: number;
  flights?: boolean;
}

function BedbankSideBarFilters(props: Props) {
  const {
    activeFilter,
    offer,
    isCalendarEnabled,
    onViewPackageOptions,
    recommendationsShown,
    flightsEnabled,
    selectedDay,
    rooms,
    duration,
    flights,
  } = props

  const dispatch = useOfferPageDispatch()
  const formRef = useRef<HTMLFormElement>(null)
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const intlDateFormatter = useIntlDateFormatter()

  const isNewMobileBookingFlowEnabled = useOptimizelyExperiment(OptimizelyExperiments.croLPPNewMobileBooking)

  const closeFilter = useCallback(() => {
    if (formRef.current?.reportValidity()) {
      dispatch({ type: OfferPageActions.closeFilter, occupancyPicker: true })
      onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
    }
  }, [dispatch, onUserAnalyticsAction])

  const filterContinue = useCallback(() => {
    if (formRef.current?.reportValidity()) {
      dispatch({ type: OfferPageActions.filterContinue })
    }
  }, [dispatch])

  const onBack = useCallback(() => {
    if (formRef.current?.reportValidity()) {
      if (recommendationsShown) {
        dispatch({ type: OfferPageActions.hideRecommendationsView })
      }

      if (isNewMobileBookingFlowEnabled) {
        dispatch({ type: OfferPageActions.filterBack })
      } else {
        dispatch({ type: OfferPageActions.toggleFilter, filter: 'all' })
      }
    }
  }, [dispatch, recommendationsShown, isNewMobileBookingFlowEnabled])

  const onApply = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (isNewMobileBookingFlowEnabled) {
      filterContinue()
    } else {
      closeFilter()
      onViewPackageOptions?.()
    }
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.Apply)
  }, [closeFilter, onViewPackageOptions, onUserAnalyticsAction, isNewMobileBookingFlowEnabled, filterContinue])

  const capacities = getCapacitiesForBedbankOffer(offer)

  // Workaround for bug in React Modal when quickly switching between open/closed
  // https://github.com/reactjs/react-modal/issues/888
  // https://github.com/reactjs/react-modal/issues/808
  // TODO: Come up with better solution, because this will always delay opening the modal
  const delayedOpen = useDelayedTogglingBoolean(!!activeFilter, { onDelay: 300 })

  const adultsNumber = useMemo(() => sum(rooms ?? [], room => room.adults), [rooms])

  const {
    total,
    saleUnit,
  } = getCalendarDisplayTotalPrice({
    price: selectedDay?.price ?? 0,
    adultsNumber,
    nights: duration,
    roomsNumber: rooms?.length ?? 1,
    withFlights: !!flights,
    flightPrice: selectedDay?.flightPrice ?? 0,
    displayPricingPer: displayPricingAs(offer.displayPricingAsPerPerson),
  })

  const onResetDates = useCallback(() => {
    dispatch({ type: OfferPageActions.resetDates })
  }, [dispatch])

  return (
    <ModalBase
      isOpen={delayedOpen}
      onClose={closeFilter}
      mode="drawer"
    >
      <form onSubmit={onApply} ref={formRef}>
        {activeFilter === 'all' && <>
          <ModalHeader
            title={`Your ${config.brandConfig.holidaySynonym}`}
            onCloseButtonClick={closeFilter}
          />
          <ModalBody>
            <FiltersBlock>
              {isCalendarEnabled ? <CalendarDatePickerSelectBox inSidebar /> : <OfferDatePickerSelectBox inSidebar />}
              <OfferCapacityWithAgesSelectBox inSidebar />
              {flightsEnabled && offer.flights?.flightsEnabled && <OfferAirportSelectBox inSidebar />}
            </FiltersBlock>
          </ModalBody>
        </>}
        {activeFilter === 'airport' && <>
          <ModalHeader
            title="Where will you depart from?"
            subtitle="Select your preferred airport"
            onCloseButtonClick={closeFilter}
            onBackButtonClick={onBack}
          />
          <ModalBody>
            <ModalContent>
              <OfferAirportOptions />
            </ModalContent>
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: 'Apply',
              type: 'submit',
            }}
          />
        </>}
        {activeFilter === 'capacity' && <>
          <ModalHeader
            title="Who is going?"
            onCloseButtonClick={closeFilter}
            onBackButtonClick={onBack}
          />
          <ModalBody>
            <OfferCapacityWithAgesSelection capacities={capacities} />
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: 'Apply',
              type: 'submit',
            }}
          />
        </>}
        {activeFilter === 'checkin' && <>
          <ModalHeader
            title="Select your dates"
            onCloseButtonClick={closeFilter}
            onBackButtonClick={onBack}
          >
          </ModalHeader>
          <ModalBody>
            <ModalContent>
              <CalendarDatePickerWrapper>
                {isCalendarEnabled ? <CalendarDatePicker single /> : <OfferDatePicker single />}
              </CalendarDatePickerWrapper>
            </ModalContent>
          </ModalBody>
          <FooterContents className={cn({ visible: !!selectedDay })}>
            <ModalFooter>
              <VerticalSpacer gap={8}>
                {selectedDay && <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
                  <BodyText variant="medium">
                    <Group direction="horizontal" verticalAlign="center">
                      <span>{intlDateFormatter(selectedDay.checkIn, INTL_SHORT_DAY_NAME_DAY_MONTH)}</span>
                      <LineArrowRightIcon size="XS" />
                      <span>{intlDateFormatter(selectedDay.checkOut, INTL_SHORT_DAY_NAME_DAY_MONTH)}</span>
                    </Group>
                  </BodyText>
                  <TextButton kind="tertiary" horizontalOutdent="end" onClick={onResetDates}>
                    Reset
                  </TextButton>
                </Group>}
                <Group direction="horizontal" horizontalAlign="space-between">
                  {selectedDay?.price && selectedDay?.hasPromo && <div>
                    <Caption variant="large">
                      {pluralizeToString('night', duration)} {flights && '+ flights '} {' from'}
                    </Caption>
                    <Heading as="span" variant="heading5">
                      <FormatCurrency value={total} />
                      {offer.displayPricingAsPerPerson && <Caption variant="medium" as="span">{saleUnit}</Caption>}
                    </Heading>{' '}
                    {!offer.displayPricingAsPerPerson && <Caption as="span" variant="large">{saleUnit}</Caption>}
                  </div>}
                  <TextButton kind="primary" size="large" type="submit" fit="mobile-full-width">
                    Apply
                  </TextButton>
                </Group>
              </VerticalSpacer>
            </ModalFooter>
          </FooterContents>
        </>}
        {activeFilter === 'roomSelection' && <BedbankRoomSelectionModal
          onClose={closeFilter}
          onBack={onBack}
        />}
        {activeFilter === 'packageSelection' && <BedbankPackageSelectionModal
          onClose={closeFilter}
          onBack={onBack}
        />}
        {activeFilter === 'roomDetails' && <BedbankRoomAndPackageDetailsModal
          onClose={closeFilter}
        />}
      </form>
    </ModalBase>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps: Partial<Props>) => ({
  flightsEnabled: selectIsBedbankFlightBookingEnabled(appState),
  isCalendarEnabled: isFlashCalendarEnabled({ ...state, bedbankOffer: ownProps.offer }, appState),
  recommendationsShown: state.showRecommendationsView && shouldEnableCalendarRecommendations(state.bedbankOffer),
  selectedDay: getSelectedDay(state, appState),
  rooms: state.rooms,
  duration: state.duration,
  flights: state.flights,
  activeFilter: state.activeFilter,
}), BedbankSideBarFilters)
